// Pure helpers for the coordinator/admin Instructors tab: which openings are visible to instructors, the settings
// they were opened with, and the "Open to instructors" wizard state. No React and no network in here.

export const LEAD_ROLE = "LEAD_INSTRUCTOR";
export const ASSISTANT_ROLE = "ASSISTANT_INSTRUCTOR";
export const DEFAULT_LIMIT = 3;
export const MAX_LIMIT = 20;
export const MAX_OPENINGS = 10;

export const WIZARD_STEPS = ["Openings", "Sign-up method", "Who can request", "Review"];

const ROLE_DETAILS = {
  [LEAD_ROLE]: { title: "Lead instructor", hint: "Runs the session, takes attendance, issues sign-offs" },
  [ASSISTANT_ROLE]: { title: "Assistant instructor", hint: "Co-teaches and runs skill stations" },
  TA: { title: "TA", hint: "Helps with setup, equipment, and scenarios" },
};

// Order matters: levels are stored and shown in this order.
export const LEVELS = [
  { key: "INSTRUCTOR", title: "Instructors", desc: "Certified instructors at your training center" },
  { key: "LEAD", title: "Lead instructors only", desc: "Instructors approved to lead this course" },
  { key: "TA", title: "Teaching assistants", desc: "Members approved to assist in class" },
];
const LEVEL_KEYS = LEVELS.map((l) => l.key);
const DEFAULT_LEVELS = ["INSTRUCTOR"];

export function roleTitle(role) {
  return ROLE_DETAILS[role]?.title || role;
}

export function roleHint(role) {
  return ROLE_DETAILS[role]?.hint || "Teaches part of the session";
}

export function sessionLabel(s) {
  return s?.name || s?.title || `Session ${s?.sessionNumber || ""}`.trim();
}

export function formatSessionDate(s) {
  const raw = s?.sessionDate || s?.date;
  if (!raw) return "No date";
  const d = new Date(raw + (raw.length <= 10 ? "T12:00:00" : ""));
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

const isOpen = (o) => o.status === "OPEN";
// Responses from before this feature carry no flag; everything that existed then was visible.
const isPosted = (o) => o.posted !== false;

export function isSessionPosted(openings) {
  return openings.some((o) => isOpen(o) && isPosted(o));
}

export function openCountsByRole(openings, roleCodes) {
  const counts = {};
  roleCodes.forEach((role) => {
    counts[role] = openings.filter((o) => isOpen(o) && o.teachingRole === role).length;
  });
  return counts;
}

function readSettings(list) {
  const approval = list.filter((o) => o.signupMode === "APPROVAL");
  const mode = approval.length ? "APPROVAL" : "FIRST_COME";
  const limit = mode === "APPROVAL" ? approval.find((o) => o.signupLimit)?.signupLimit ?? null : null;
  const seen = new Set(list.flatMap((o) => o.eligibleLevels || []));
  const levels = LEVEL_KEYS.filter((k) => seen.has(k));
  return { mode, limit, levels: levels.length ? levels : DEFAULT_LEVELS };
}

/** The sign-up method, limit and levels the posted openings carry, or null when nothing is posted. */
export function currentSettings(openings) {
  const posted = openings.filter((o) => isOpen(o) && isPosted(o));
  return posted.length ? readSettings(posted) : null;
}

export function signupSummaryText({ mode, limit }) {
  if (mode === "FIRST_COME") return "First come, first served";
  return limit ? `Up to ${limit} sign-up${limit > 1 ? "s" : ""}` : "Unlimited sign-ups";
}

export function startWizard({ openings, roleCodes }) {
  const posted = currentSettings(openings);
  const closed = openings.filter((o) => isOpen(o) && o.signupMode === "APPROVAL");
  const saved = posted || (closed.length ? readSettings(closed) : null);
  const limit = saved?.limit ?? null;
  return {
    scope: "one",
    step: 0,
    edit: Boolean(posted),
    counts: openCountsByRole(openings, roleCodes),
    mode: saved?.mode || "APPROVAL",
    limit,
    max: limit || DEFAULT_LIMIT,
    levels: saved?.levels || DEFAULT_LEVELS,
  };
}

export function wizardTotal(state) {
  return Object.values(state.counts).reduce((sum, n) => sum + n, 0);
}

/** The message that blocks leaving this step, or an empty string. */
export function stepError(state, step) {
  if (step === 0 && wizardTotal(state) < 1) return "Add at least one opening.";
  if (step === 2 && state.levels.length === 0) return "Choose at least one level.";
  return "";
}

export function toggleLevel(levels, key) {
  const next = new Set(levels);
  if (next.has(key)) next.delete(key); else next.add(key);
  return LEVEL_KEYS.filter((k) => next.has(k));
}

export function buildPostPayload(state) {
  // For every upcoming session the counts came from one session, so a 0 would delete other sessions' open slots:
  // send only the roles that are wanted, and leave the rest of each session alone.
  const counts = state.scope === "all"
    ? Object.fromEntries(Object.entries(state.counts).filter(([, n]) => n > 0))
    : { ...state.counts };
  const payload = {
    openings: counts,
    signupMode: state.mode,
    eligibleLevels: [...state.levels],
  };
  if (state.mode === "APPROVAL" && state.limit != null) payload.signupLimit = state.limit;
  return payload;
}

function openingsText(counts) {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .map(([role, n]) => {
      const name = role === "TA" ? "TA" : roleTitle(role).toLowerCase();
      return `${n} ${name}${n > 1 ? "s" : ""}`;
    })
    .join(", ");
}

/** The review step: one row per decision, each pointing back at the step that changes it. */
export function reviewRows(state, { session, sessionCount }) {
  const sessions = state.scope === "all"
    ? `All ${sessionCount} sessions`
    : `${sessionLabel(session)} · ${formatSessionDate(session)}`;
  const method = state.mode === "FIRST_COME"
    ? "First come, first served"
    : `Request and approval${state.limit ? ` · up to ${state.limit} requests` : ""}`;
  const who = LEVELS.filter((l) => state.levels.includes(l.key)).map((l) => l.title).join(", ");
  return [
    { label: "Sessions", value: sessions, step: 0 },
    { label: "Openings", value: openingsText(state.counts), step: 0 },
    { label: "Sign-up method", value: method, step: 1 },
    { label: "Who can request", value: who, step: 2 },
  ];
}

/** Per-role counts for adding one more unassigned opening of {@code role}. */
export function countsAfterAdd(openings, roleCodes, role) {
  const counts = openCountsByRole(openings, roleCodes);
  return { ...counts, [role]: (counts[role] || 0) + 1 };
}

/** "Sep 3 – Oct 1" for sessions already in date order. */
export function formatSessionRange(sessions) {
  if (!sessions.length) return "";
  const short = (s) => new Date(`${(s.sessionDate || s.date).slice(0, 10)}T12:00:00`)
    .toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const first = short(sessions[0]);
  const last = short(sessions[sessions.length - 1]);
  return first === last ? first : `${first} – ${last}`;
}

export function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Sessions that can still be changed (today or later), in date order. */
export function upcomingSessions(sessions, today = todayIso()) {
  return sessions
    .filter((s) => (s.sessionDate || s.date) && (s.sessionDate || s.date).slice(0, 10) >= today)
    .sort((a, b) => (a.sessionDate || a.date).localeCompare(b.sessionDate || b.date));
}
